import { SearchComparisonDateTime } from "@components/common/calendar/date-range-picker/Interfaces";
import {
  RankPeopleCountsDataChart,
  DataItemSeries
} from "@components/widgets/rank-peoplecounts/widget/Interfaces";
import {
  CalendarModeRangeType,
  ChartType,
  TimeInterval
} from "@custom-types/Types";
import { CHART_TYPE, LIMIT_WORD, TIME_INTERVAL } from "@resources/Constants";
import { Theme } from "@themes/Types";
import {
  chartTooltipFormatter,
  getAxisIntervalLabels,
  numberSymbol,
  xAxisLabelFormatter
} from "@utils/Widget";
import type {
  DefaultLabelFormatterCallbackParams,
  EChartsOption,
  TooltipComponentFormatterCallbackParams
} from "echarts";
import { isArray, isNumber } from "lodash";
import trophy1 from "@resources/img/report/report-widget-top-count-1.png";
import trophy2 from "@resources/img/report/report-widget-top-count-2.png";
import trophy3 from "@resources/img/report/report-widget-top-count-3.png";
import { commonTooltipSettings } from "@components/report/widget/widget-statistics/helper";
import { theme as HVTheme } from "@repo/HVComponents";

const trophyIcons = {
  trophy1,
  trophy2,
  trophy3
};

const tooltipSetting = (
  showSymbol?: boolean,
  fixValue?: number
): EChartsOption["tooltip"] => ({
  trigger: "axis",
  formatter: (params: TooltipComponentFormatterCallbackParams) => {
    if (isArray(params)) {
      return `
      <ul class="widget-rank-peoplecounts-tooltip">
        <li class="widget-rank-peoplecounts-tooltip__item">
          <span>
            ${params[0].marker}
            <span>${params[0].seriesName}</span>
          </span>
          <span>${
            fixValue
              ? Number(params[0].value).toFixed(fixValue)
              : params[0].value
          }${showSymbol ? `s` : ``}</span>
        </li>
      <ul>
    `;
    }
    return "";
  },

  backgroundColor: commonTooltipSettings().backgroundColor,
  borderColor: commonTooltipSettings().borderColor,
  borderRadius: commonTooltipSettings().borderRadius,
  textStyle: {
    fontSize: 12,
    fontFamily: HVTheme.fontFamily,
    color: commonTooltipSettings().textStyle.color
  },
  padding: commonTooltipSettings().padding
});

const tooltipLineSetting = (
  timeInterval: TimeInterval,
  mode: CalendarModeRangeType,
  fixValue?: number
): EChartsOption["tooltip"] => ({
  trigger: "axis",
  appendToBody: true,
  position(point) {
    const x = point[0];
    const y = point[1];
    return [x, y];
  },
  formatter: (params: TooltipComponentFormatterCallbackParams) => {
    if (isArray(params)) {
      return chartTooltipFormatter(
        timeInterval,
        params[0].name,
        params.map((item) => ({
          label: String(item.seriesName),
          data: String(item.data),
          color: String(item.color)
        })),
        "",
        mode,
        fixValue
      );
    }
    return "";
  },
  ...commonTooltipSettings()
});

export const chartRankPeopleCounts = (
  theme: Theme,
  expansionChartType: ChartType,
  data: RankPeopleCountsDataChart[],
  _labelYAxis: string,
  searchComparisonDateTime: SearchComparisonDateTime,
  timeInterval: TimeInterval = TIME_INTERVAL.HOUR_1,
  forReport = false,
  enableDataZoom = false,
  forAnalytics = false,
  seriesName = "Count",
  fixValue = undefined as number | undefined,
  showSymbol = false
): EChartsOption => {
  const length = data.length;
  const colorsRange = [
    theme.widget.rankPeopleCounts.graphLineColor1,
    theme.widget.rankPeopleCounts.graphLineColor2,
    theme.widget.rankPeopleCounts.graphLineColor3,
    theme.widget.rankPeopleCounts.graphLineColor4,
    theme.widget.rankPeopleCounts.graphLineColor5,
    theme.widget.rankPeopleCounts.graphLineColor6,
    theme.widget.rankPeopleCounts.graphLineColor7,
    theme.widget.rankPeopleCounts.graphLineColor8,
    theme.widget.rankPeopleCounts.graphLineColor9,
    theme.widget.rankPeopleCounts.graphLineColor10
  ];

  const colorsFull =
    length - colorsRange.length > 0
      ? Array.from(
          { length: length - colorsRange.length },
          () => theme.widget.rankPeopleCounts.mono4
        ).concat(colorsRange)
      : colorsRange;

  data.map((item: RankPeopleCountsDataChart, index: number) => {
    const items = item;
    items.itemStyle = { color: colorsFull[index] };
    return items;
  });

  if (expansionChartType === CHART_TYPE.PIE_DOUGHNUT) {
    return {
      series: [
        {
          name: "Busiest locations",
          type: "pie",
          color: colorsFull,
          radius: ["45%", "90%"],
          label: {
            show: true,
            position: "inner",
            formatter: (params) => {
              if (isNumber(params.percent) && params.percent < 10) {
                return params.percent ? `{d|${params.percent}}%` : "";
              }
              return params.percent
                ? `{a|${
                    params.name.length > 4
                      ? params.name.substring(0, 4).concat("...")
                      : params.name
                  }}\n{d|${params.percent}}%`
                : "";
            },
            rich: {
              d: {
                fontSize: 11,
                fontWeight: "bold"
              }
            },
            fontSize: 10,
            fontFamily: HVTheme.fontFamily,
            color: theme.widget.rankPeopleCounts.white,
            lineHeight: 17,
            textBorderWidth: 2,
            textBorderColor: theme.widget.rankPeopleCounts.borderLabel
          },
          data,
          emphasis: {
            scaleSize: 0,
            itemStyle: {
              shadowColor: "rgba(0, 0, 0, 0.25)",
              shadowBlur: 2,
              shadowOffsetY: 7
            }
          }
        }
      ]
    };
  }
  if (expansionChartType === CHART_TYPE.PIE_DOUGHNUT_THIN) {
    const total = data.reduce(
      (sum: number, current: RankPeopleCountsDataChart): number => {
        return sum + (current.value as number);
      },
      0
    );
    const uniqueData = data.map((item, index) => ({
      ...item,
      legendName: `${item.name}_${index}`
    }));

    return {
      legend: {
        bottom: 0,
        show: true,
        icon: "circle",
        height: 25,
        orient: "horizontal",
        data: uniqueData.map((item) => item.legendName),
        selectedMode: false,
        type: "scroll",
        pageIconSize: 0,
        pageFormatter: "...",
        pageButtonGap: -8,
        pageButtonItemGap: 0,
        pageTextStyle: {
          color: theme.widget.rankPeopleCounts.textPrimary
        },
        formatter: (params) => {
          const legenData = uniqueData.find((row) => row.legendName === params);
          if (!legenData) return params;

          const value = (legenData.value as number) ?? 0;
          const percent = value > 0 ? Math.round((value / total) * 100) : 0;

          return `${
            legenData.name.length > 4
              ? legenData.name.substring(0, 4).concat("...")
              : legenData.name
          } {a|${percent}%}`;
        },
        itemWidth: 6,
        itemHeight: 6,
        itemGap: 10,
        textStyle: {
          color: theme.widget.rankPeopleCounts.textPrimary,
          overflow: "truncate",
          fontSize: "12px",
          rich: {
            a: {
              fontWeight: "bold"
            }
          }
        }
      },
      series: [
        {
          name: "Busiest locations",
          type: "pie",
          color: colorsFull,
          radius: ["75%", "90%"],
          bottom: 20,
          label: {
            show: false,
            position: "center",
            formatter: (params) => {
              const dataItem = params.data as DataItemSeries;
              if (isNumber(params.percent) && dataItem) {
                return `{d|${Math.round(params.percent)}%}\n{a|${
                  dataItem.actualName.length > 8
                    ? dataItem.actualName.substring(0, 8).concat("...")
                    : dataItem.actualName
                }} {b||} {a|${dataItem.value}}`;
              }
              return "";
            },
            rich: {
              b: {
                color: theme.widget.rankPeopleCounts.borderModal
              },
              a: {
                fontSize: 12,
                color: theme.widget.rankPeopleCounts.textSecondary
              },
              d: {
                fontSize: 28,
                color: theme.widget.rankPeopleCounts.textPrimary
              }
            },
            fontFamily: HVTheme.fontFamily
          },
          data: uniqueData.map((item) => ({
            name: item.legendName,
            value: item.value,
            itemStyle: item.itemStyle,
            actualName: item.name
          })),
          itemStyle: {
            borderWidth: 2,
            borderColor: theme.widget.rankPeopleCounts.backgroundColorTooltip
          },
          emphasis: {
            label: {
              show: true
            }
          }
        }
      ]
    };
  }
  if (
    expansionChartType === CHART_TYPE.LINE &&
    data &&
    Array.isArray(data[0]?.value)
  ) {
    const { mode, start, end } = searchComparisonDateTime;
    const result = getAxisIntervalLabels(mode, start, end, timeInterval);
    const { labels: xAxisLabels } = result;

    return {
      color: colorsFull,
      tooltip: tooltipLineSetting(timeInterval, mode, fixValue),
      dataZoom: enableDataZoom
        ? [
            {
              type: "inside"
            }
          ]
        : [],
      grid: {
        left: 20,
        right: 25,
        bottom: "0",
        top: 35,
        containLabel: true,
        show: true,
        borderColor: "transparent"
      },
      xAxis: [
        {
          type: "category",
          nameTextStyle: {
            color: theme.widget.rankPeopleCounts.textPrimary,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            verticalAlign: "top",
            lineHeight: 25,
            padding: [1, 0, 0, 0]
          },
          nameGap: 10,
          data: xAxisLabels,
          boundaryGap: false,
          axisLabel: {
            color: theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            formatter: xAxisLabelFormatter(timeInterval, mode),
            rotate: 30
          },
          axisLine: {
            show: false
          },
          axisTick: {
            lineStyle: {
              color: theme.widget.rankPeopleCounts.graphDefaultLine,
              width: 1
            }
          }
        }
      ],
      yAxis: [
        {
          type: "value",
          nameGap: 12,
          axisLabel: {
            color: theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            align: "right"
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: theme.widget.rankPeopleCounts.graphDefaultLine,
              width: 1
            }
          }
        }
      ],
      series: data.map((item, index) => ({
        name: item.name,
        type: "line",
        data: item.value as number[],
        itemStyle: { color: colorsFull[index] },
        lineStyle: {
          width: 2
        },
        symbolSize: 4
      }))
    };
  }
  if (expansionChartType === CHART_TYPE.BAR_HORIZONTAL) {
    const yAxisLabels = data.map((item) => item.name);
    const getTrophyIndex = (index: number) => {
      if (index + 1 === yAxisLabels.length) {
        return 1;
      }
      if (index === yAxisLabels.length - 2) {
        return 2;
      }
      if (index === yAxisLabels.length - 3) {
        return 3;
      }

      return yAxisLabels.length - index;
    };

    return {
      title: {
        show: false
      },
      color: colorsFull,
      tooltip: tooltipSetting(showSymbol, fixValue),
      grid: {
        left: forReport ? "auto" : 10,
        right: forReport ? 40 : 25,
        bottom: 0,
        top: forAnalytics ? "auto" : 25,
        containLabel: true
      },
      xAxis: [
        {
          type: "value",
          nameTextStyle: {
            color: forAnalytics
              ? theme.chart.colorNewAxisLabel
              : theme.widget.rankPeopleCounts.textPrimary,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            align: "right",
            padding: [0, 0, -5, 0]
          },
          nameRotate: -90,
          nameGap: 12,
          axisLabel: {
            color: forAnalytics
              ? theme.chart.colorNewAxisLabel
              : theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            align: "center",
            rotate: forReport ? 0 : 30,
            ...(forReport
              ? {
                  showMaxLabel: true,
                  formatter: (value) => {
                    const [number, symbol] = numberSymbol(value);
                    const label = `{value|${number}${symbol}}`;
                    return label;
                  },
                  rich: {
                    value: {
                      lineHeight: 20,
                      align: "center",
                      padding: [0, 0, 7, 0]
                    }
                  }
                }
              : {})
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: forAnalytics
                ? theme.chart.graphDefaultLine
                : theme.widget.rankPeopleCounts.graphDefaultLine,
              width: 1
            }
          }
        }
      ],
      yAxis: [
        {
          type: "category",
          nameGap: 10,
          data: yAxisLabels.reverse(),
          boundaryGap: true,
          axisLabel: {
            color: forAnalytics
              ? theme.chart.colorNewText
              : theme.chart.colorAxisLabel,
            fontSize: "12px",
            fontFamily: HVTheme.fontFamily,
            width: forAnalytics ? 102 : 70,
            overflow: "truncate",
            ...(forReport
              ? {
                  showMaxLabel: true,
                  formatter: (value, i) => {
                    const index = getTrophyIndex(i);
                    const limitWord = forAnalytics
                      ? LIMIT_WORD.ELEVEN
                      : LIMIT_WORD.FIVE;
                    const truncatedValue =
                      value.length > limitWord
                        ? `${value.slice(0, limitWord)}...`
                        : value; // Adjust length as needed
                    const stringifyIndex = forAnalytics
                      ? String(index)
                      : String(index).padStart(3, " ");
                    let label =
                      index < 4
                        ? `{trophy${index}|}{value|${truncatedValue}}`
                        : `{stringifyIndex|${stringifyIndex}}{value|${truncatedValue}}`;
                    label = forAnalytics
                      ? `{stringifyIndex|${stringifyIndex}}{value|${truncatedValue}}`
                      : label;
                    return label;
                  },
                  rich: {
                    value: {
                      lineHeight: 20,
                      padding: forAnalytics ? [0, 0, 0, 0] : [0, 0, 0, 5],
                      width: forAnalytics ? 82 : 50,
                      align: "left"
                    },
                    stringifyIndex: {
                      lineHeight: 20,
                      padding: [0, 0, 0, 0],
                      width: 20,
                      align: "left",
                      fontWeight: forAnalytics ? "bold" : "normal"
                    },
                    trophy1: {
                      width: 20,
                      height: 20,
                      align: "left",
                      backgroundColor: {
                        image: trophyIcons.trophy1
                      }
                    },
                    trophy2: {
                      width: 20,
                      height: 20,
                      align: "left",
                      backgroundColor: {
                        image: trophyIcons.trophy2
                      }
                    },
                    trophy3: {
                      width: 20,
                      height: 20,
                      align: "left",
                      backgroundColor: {
                        image: trophyIcons.trophy3
                      }
                    }
                  }
                }
              : {})
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          }
        }
      ],
      series: [
        {
          name: seriesName,
          type: "bar",
          data: data
            .map((item, index) => ({
              name: item.name,
              value: item.value,
              itemStyle: { color: item.defaultColor || colorsFull[index] }
            }))
            .reverse(),
          barWidth: 8,
          colorBy: "data",
          ...(forReport
            ? {
                label: {
                  show: true,
                  position: "right",
                  color: theme.chart.colorAxisLabel,
                  formatter: (params: DefaultLabelFormatterCallbackParams) => {
                    const [number, symbol] = numberSymbol(
                      params.value as string
                    );
                    return `${number}${symbol}`;
                  }
                }
              }
            : {})
        }
      ]
    };
  }
  return {};
};
